import React, { useEffect, useState, Fragment } from "react";
import ReactDatatable from "@ashvin27/react-datatable";
import { postMethod } from "../../../../_services/_postMethod";
import Loader from "../../../modules/loader";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import BulkProductImport from "./BulkProductImport";
import ProductDetails from "./ProductDetails";
import Barcode from "react-barcode";
import { QRCodeCanvas } from "qrcode.react";
import * as XLSX from "xlsx";
import html2pdf from "html2pdf.js";


const Product = (props) => {
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState();
  const [importPreviewList, setImportPreviewList] = useState([]);
  const [tableState, setTableState] = useState({
    loading: true,
    totalPages: 0,
    records: [],
  });
  const [expandedRows, setExpandedRows] = useState({});

  const branchType = localStorage.getItem("branch_type");

  const toggleImportModal = () => setShowImportModal(!showImportModal);

  // Download all products in Excel
  const downloadExcel = async () => {
    try {
      setDownloadingExcel(true);
      const res = await postMethod("api/masters/product/web-browse/?", {});
      const products = res?.Data || [];

      if (products.length === 0) {
        alert("No products available to download");
        setDownloadingExcel(false);
        return;
      }

      const headers = [
        "S.No",
        "Product Code",
        "Product Name",
        "Category Name",
        "Size",
        "Color",
        "Image",
        "Balance Qty",
      ];

      const sheetData = [
        headers,
        ...products.map((item, index) => {
          const imgSrc = item.image_path
            ? item.isImageUrl === true
              ? item.image_path
              : (item.url_image || "") + item.image_path
            : "No Image";

          return [
            index + 1,
            item.product_code || "",
            item.product_name || "",
            item.category_name || "",
            item.size_length || "-",
            item.color || "-",
            imgSrc,
            item.balance_qty != null ? item.balance_qty : (item.qty || 0),
          ];
        }),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      worksheet["!cols"] = [
        { wch: 6 },
        { wch: 20 },
        { wch: 28 },
        { wch: 20 },
        { wch: 12 },
        { wch: 14 },
        { wch: 35 },
        { wch: 14 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
      const dateStr = new Date().toLocaleDateString("en-GB").replace(/\//g, "-");
      XLSX.writeFile(workbook, `Products_List_${dateStr}.xlsx`);
    } catch (err) {
      console.error("Excel download error:", err);
      alert("Failed to download Excel");
    } finally {
      setDownloadingExcel(false);
    }
  };

  const loadImageAsBase64 = async (src) => {
    if (!src) return null;
    const cleanSrc = src.trim();
    if (cleanSrc.startsWith("data:")) return cleanSrc;

    // 1. Try Direct Fetch
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 3000);
      const res = await fetch(cleanSrc, { signal: controller.signal });
      clearTimeout(timer);
      if (res.ok) {
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      }
    } catch (e) { }

    // 2. Try Image element with canvas
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width || 60;
          canvas.height = img.naturalHeight || img.height || 60;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", 0.85));
        } catch (e) {
          resolve(cleanSrc);
        }
      };
      img.onerror = () => resolve(null);
      img.src = cleanSrc;
    });
  };

  // Download all products in PDF
  const downloadPDF = async () => {
    try {
      setDownloadingPdf(true);
      const res = await postMethod("api/masters/product/web-browse/?", {});
      const products = res?.Data || [];

      if (products.length === 0) {
        alert("No products available to download");
        setDownloadingPdf(false);
        return;
      }

      // Preload all images to Base64 in parallel
      const loadedImages = await Promise.all(
        products.map((p) => {
          const imgSrc = p.image_path
            ? p.isImageUrl === true
              ? p.image_path
              : (p.url_image || "") + p.image_path
            : null;
          return loadImageAsBase64(imgSrc);
        })
      );

      const dateStr = new Date().toLocaleDateString("en-GB");
      const tempDiv = document.createElement("div");
      tempDiv.id = "products-pdf-export-container";
      tempDiv.style.padding = "20px";
      tempDiv.style.fontFamily = "Arial, sans-serif";
      tempDiv.style.background = "#ffffff";
      tempDiv.style.color = "#333333";

      const rowsHtml = products
        .map((p, idx) => {
          const qty = p.balance_qty != null ? p.balance_qty : (p.qty || 0);
          const isQtyZero = Number(qty) === 0;
          const base64Img = loadedImages[idx];

          return `
            <tr style="border-bottom: 1px solid #e0e0e0; ${idx % 2 === 1 ? "background-color: #f9fafb;" : ""}">
              <td style="padding: 6px 8px; text-align: center; font-size: 11px; vertical-align: middle;">${idx + 1}</td>
              <td style="padding: 6px 8px; font-weight: bold; font-size: 11px; vertical-align: middle;">${p.product_code || "-"}</td>
              <td style="padding: 6px 8px; font-size: 11px; vertical-align: middle;">${p.product_name || "-"}</td>
              <td style="padding: 6px 8px; font-size: 11px; vertical-align: middle;">${p.category_name || "-"}</td>
              <td style="padding: 6px 8px; font-size: 11px; text-align: center; vertical-align: middle;">${p.size_length || "-"}</td>
              <td style="padding: 6px 8px; font-size: 11px; text-align: center; vertical-align: middle;">${p.color || "-"}</td>
              <td style="padding: 6px 8px; text-align: center; vertical-align: middle;">
                ${base64Img
              ? `<img src="${base64Img}" width="40" height="40" style="width: 40px; height: 40px; object-fit: cover; border-radius: 4px; display: inline-block; vertical-align: middle;" />`
              : `<span style="font-size: 10px; color: #888;">No Image</span>`
            }
              </td>
              <td style="padding: 6px 8px; font-size: 11px; text-align: center; font-weight: bold; vertical-align: middle; color: ${isQtyZero ? "#d32f2f" : "#2e7d32"};">
                ${qty}
              </td>
            </tr>
          `;
        })
        .join("");

      tempDiv.innerHTML = `
        <div style="margin-bottom: 15px; border-bottom: 2px solid #1a365d; padding-bottom: 10px; display: flex; justify-content: space-between; align-items: flex-end;">
          <div>
            <h2 style="margin: 0; color: #1a365d; font-size: 20px; font-weight: bold;">Products List</h2>
            <p style="margin: 4px 0 0; color: #666; font-size: 12px;">Total Products: <strong>${products.length}</strong></p>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0; color: #666; font-size: 12px;">Date: <strong>${dateStr}</strong></p>
          </div>
        </div>

        <table style="width: 100%; border-collapse: collapse; margin-top: 10px; font-family: Arial, sans-serif;">
          <thead>
            <tr style="background-color: #1a365d; color: #ffffff;">
              <th style="padding: 8px 6px; font-size: 11px; text-align: center; width: 5%;">#</th>
              <th style="padding: 8px 6px; font-size: 11px; text-align: left; width: 18%;">Product Code</th>
              <th style="padding: 8px 6px; font-size: 11px; text-align: left; width: 25%;">Product Name</th>
              <th style="padding: 8px 6px; font-size: 11px; text-align: left; width: 17%;">Category Name</th>
              <th style="padding: 8px 6px; font-size: 11px; text-align: center; width: 9%;">Size</th>
              <th style="padding: 8px 6px; font-size: 11px; text-align: center; width: 9%;">Color</th>
              <th style="padding: 8px 6px; font-size: 11px; text-align: center; width: 9%;">Image</th>
              <th style="padding: 8px 6px; font-size: 11px; text-align: center; width: 8%;">Balance Qty</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
      `;

      const opt = {
        margin: [8, 8, 8, 8],
        filename: `Products_List_${dateStr.replace(/\//g, "-")}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      await html2pdf().set(opt).from(tempDiv).save();
    } catch (err) {
      console.error("PDF download error:", err);
      alert("Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Save bulk imported products
  const saveBulkData = () => {
    postMethod("api/masters/product/bulk_insert", { product_list: importPreviewList })
      .then((data) => {
        if (data.data[0].valid) {
          setShowImportModal(false);
          setImportPreviewList([]);
          fetchData();
        }
      });
  };

  // Import Excel preview
  const previewImportData = () => {
    postMethod("api/masters/product/PreviewImportFile", { importData })
      .then((data) => setImportPreviewList(data.data));
  };

  // Download sample Excel
  const downloadSample = () => {
    postMethod("api/masters/product/download_sample", {}).then((data) => {
      const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${data.data[0].data}`;
      const link = document.createElement("a");
      link.href = linkSource;
      link.download = "Import_Products_Template.xlsx";
      link.click();
    });
  };

  // Navigate to edit page
  const editRecord = (productId) => {
    const encodedId = window.btoa(productId);
    props.history.push(`/branch/product/add?id=${encodedId}`);
  };

  // Fetch product data
  const fetchData = (queryString = "") => {
    setTableState((prev) => ({ ...prev, loading: true }));
    postMethod(`api/masters/product/web-browse/?${queryString}`, {}).then((res) => {
      const grouped = {};

      res.Data.forEach((item) => {

        // Last part hata do (/01, /02, /03)
        const codeParts = item.product_code.split("/");
        const key = codeParts.slice(0, -1).join("/");

        if (!grouped[key]) {
          grouped[key] = [];
        }

        grouped[key].push(item);
      });

      const parentRecords = Object.keys(grouped).map((key) => {

        const children = grouped[key].sort((a, b) => {
          const aNo = parseInt(a.product_code.split("/").pop()) || 0;
          const bNo = parseInt(b.product_code.split("/").pop()) || 0;
          return aNo - bNo;
        });

        return {
          ...children[0],
          _children: children.length > 1 ? children.slice(1) : [],
        };
      });
      setTableState({
        loading: false,
        totalPages: res.totalRecords,
        records: parentRecords,
      });
    });
  };

  // Handle table events (pagination, filter, sort)
  const tableChangeHandler = (data) => {
    const queryString = Object.keys(data)
      .map((key) => {
        if (key === "sort_order" && data[key]) {
          return `sort_order=${data[key].order}&sort_column=${data[key].column}`;
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`;
      })
      .join("&");
    fetchData(queryString);
  };

  // Build display records for Retailer (with expandable rows)
  const getDisplayRecords = () => {
    return tableState.records.flatMap((rec) => {
      const parentRow = { ...rec, row_id: rec.product_code + "_p" };
      if (expandedRows[rec.product_code] && rec._children) {
        const children = rec._children.map((child, idx) => ({
          ...child,
          isChild: true,
          row_id: rec.product_code + "_c" + idx,
        }));
        return [parentRow, ...children];
      }
      return [parentRow];
    });
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
    setLoading(false);
  }, []);

  // Table configuration
  const config = {
    key_column: "row_id",
    page_size: 10,
    length_menu: [10, 20, 50],
    show_filter: true,
    show_pagination: true,
    pagination: "advance",
    no_data_text: "No data available!",
  };

  // Columns for Retailer table
  const columnsRetailer = [
    {
      key: "product_code",
      text: "Product Code",
      align: "left",
      sortable: true,
      cell: (record) => {
        // If it's a child row → no dropdown
        if (record.isChild) {
          return <span className="ml-4">{record.product_code}</span>;
        }

        // Check if this parent actually has children
        const hasChildren = record._children && record._children.length > 0;

        return (
          <span
            style={{ cursor: hasChildren ? "pointer" : "default", fontWeight: "500" }}
            onClick={() => {
              if (!hasChildren) return; // prevent toggle if no children
              setExpandedRows((prev) => ({
                ...prev,
                [record.product_code]: !prev[record.product_code],
              }));
            }}
          >
            {record.product_code}{" "}
            {hasChildren && (
              <small style={{ color: "#888" }}>
                {expandedRows[record.product_code] ? "▼" : "▶"}
              </small>
            )}
          </span>
        );
      },
    },

    { key: "product_name", text: "Product Name", sortable: true },
    { key: "category_name", text: "Category Name", sortable: true },
    {
      key: "size_length",
      text: "Size",
      sortable: true
    },
    {
      key: "color",
      text: "Color",
      sortable: true
    },
    {
      key: "image",
      text: "Image",
      cell: (record) => {
        if (!record.image_path) {
          return "No Image";
        }

        if (record.isImageUrl === true) {
          // agar image_path already full URL hai
          return (
            <img
              src={record.image_path}
              width="40px"
              alt="product"
            />
          );
        } else {
          // agar base URL + image_path banana hai
          return (
            <img
              src={record.url_image + record.image_path}
              width="40px"
              alt="product"
            />
          );
        }
      },
    },



    {
      key: "balance_qty",
      text: "Balance Qty",
      cell: (record) =>
        record.balance_qty === 0 ? (
          <label className="text-danger">0</label>
        ) : (
          <label className="text-success">{record.balance_qty}</label>
        ),
    },



    {
      key: "action",
      text: "Action",
      cell: (record) => {
        if (record.cb_product === false) {
          return (
            <div>
              <ProductDetails data={record.product_code} />
            </div>
          );
        } else {
          return (
            <div>
              <button
                className="btn btn-link btn-sm"
                onClick={() => editRecord(record.product_id)}
              >
                <i className="fa fa-edit"></i>
              </button>
              <ProductDetails data={record.product_code} />
            </div>
          );
        }
      },
    },
    {
      key: "bar_code",
      text: "Bar Code",
      cell: (record) => {
        // jis value se scan karna ho (product_code / bar_code)
        const barcodeValue = record.product_code;

        if (!barcodeValue) {
          return "N/A";
        }

        return (
          <div style={{ background: "#fff", padding: "1px" }}>
            <Barcode
              value={barcodeValue}
              width={0.6}     // 👈 width kam
              height={40}
              fontSize={10}
              displayValue={false}   // number neeche nahi dikhana ho toh
            />
          </div>
        );
      },
    },

    {
      key: "qr_code",
      text: "QR Code",
      cell: (record) => {
        const value = record.product_code;
        if (!value) return "N/A";

        return (
          <QRCodeCanvas
            value={value}
            size={50}
            level="M"
          />
        );
      },
    },
  ];
  const [showImportModal1, setShowImportModal1] = useState(false);
  return (
    <>
      {loading && <Loader />}

      {/* Import Modal */}

      <Modal
        size="xl"
        backdrop="static"
        isOpen={showImportModal}
        toggle={toggleImportModal}
        style={{ marginTop: "100px" }}
      >
        <ModalHeader toggle={toggleImportModal}>Import Product</ModalHeader>
        <ModalBody style={{ maxHeight: "400px", overflow: "auto" }}>
          <div className="row mb-3">
            <div className="col-md-4">
              <input
                className="form-control p-1"
                type="file"
                onChange={(e) => {
                  const reader = new FileReader();
                  reader.onload = (ev) => setImportData(ev.target.result);
                  reader.readAsDataURL(e.target.files[0]);
                }}
              />
            </div>
            <div className="col-md-6">
              <Button color="info" onClick={previewImportData}>
                Preview
              </Button>
              <a
                onClick={downloadSample}
                className="btn btn-sm btn-outline-success ml-2"
              >
                <i className="flaticon2-download"></i> Download Sample
              </a>
            </div>
          </div>

          {/* Preview Table */}
          <div style={{ overflowX: "auto" }}>
            <table className="table table-bordered table-striped table-hover">
              <thead>
                <tr>
                  {importPreviewList[0] &&
                    Object.keys(importPreviewList[0]).map((key, idx) => (
                      <th key={idx}>{key}</th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {importPreviewList.map((item, idx) => (
                  <tr key={idx}>
                    {Object.values(item).map((val, i) => (
                      <td key={i}>{val}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ModalBody>
        <ModalFooter>
          {importPreviewList.length > 0 && (
            <Button color="primary" onClick={saveBulkData}>
              Save
            </Button>
          )}
          <Button color="secondary" onClick={toggleImportModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Products Table */}
      <div className="card card-custom gutter-b">
        <div className="card-header">
          <div className="card-title">
            <h3 className="card-label">Products</h3>
          </div>
          <div className="card-toolbar">
            <a
              onClick={downloadPDF}
              className="btn btn-sm btn-outline-danger mr-3"
              style={{ cursor: "pointer", pointerEvents: downloadingPdf ? "none" : "auto" }}
            >
              <i className="flaticon2-document"></i> {downloadingPdf ? "Generating PDF..." : "Download PDF"}
            </a>
            <a
              onClick={downloadExcel}
              className="btn btn-sm btn-outline-success mr-3"
              style={{ cursor: "pointer", pointerEvents: downloadingExcel ? "none" : "auto" }}
            >
              <i className="flaticon2-download"></i> {downloadingExcel ? "Generating Excel..." : "Download Excel"}
            </a>
            <a
              onClick={() => setShowImportModal1(true)}
              className="btn btn-sm btn-outline-info mr-3"
            >
              <i className="flaticon2-download"></i> Import
            </a>
            <a
              href="/branch/product/add"
              className="btn btn-sm btn-outline-theme"
            >
              <i className="flaticon2-user"></i> Add Product
            </a>
          </div>
        </div>
        <div className="card-body">

          <ReactDatatable
            config={config}
            records={getDisplayRecords()}
            columns={columnsRetailer}
            dynamic={true}
            total_record={tableState.totalPages}
            onChange={tableChangeHandler}
            loading={tableState.loading}
          />

          <div>
            <BulkProductImport
              isOpen={showImportModal1}
              toggle={() => setShowImportModal1(false)}
              branchId={localStorage.getItem("jwl_branch_id")}  // dynamic from user session
              userId={1}    // dynamic from token or login
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Product;
